import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, Box, Tooltip,
  Menu, MenuItem, IconButton, Drawer, List, ListItem,
  ListItemButton, ListItemText, Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

function Navbar() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [avatarError, setAvatarError] = useState(false);
  const [docsAnchorEl, setDocsAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const docsMenuOpen = Boolean(docsAnchorEl);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const email = localStorage.getItem('userEmail');
    setIsLoggedIn(!!token);
    setUserEmail(email || '');

    const handleAuthChange = () => {
      const token = localStorage.getItem('token');
      const email = localStorage.getItem('userEmail');
      setIsLoggedIn(!!token);
      setUserEmail(email || '');
    };

    window.addEventListener('authChange', handleAuthChange);
    return () => window.removeEventListener('authChange', handleAuthChange);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    setIsLoggedIn(false);
    window.dispatchEvent(new Event('authChange'));
    navigate('/');
  };

  const handleDocsMenuOpen = (event) => setDocsAnchorEl(event.currentTarget);
  const handleDocsMenuClose = () => setDocsAnchorEl(null);

  const handleDocsClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    handleDocsMenuClose();
  };

  const handleMobileClose = () => setMobileOpen(false);

  // ── Mobile drawer ────────────────────────────────────────────────────────────
  const mobileDrawer = (
    <Box sx={{ width: 260, bgcolor: '#0a0a0a', height: '100%', color: 'white' }}>
      <List>
        {isLoggedIn ? (
          <>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/dashboard" onClick={handleMobileClose}
                sx={{ color: 'rgba(255,255,255,0.8)' }}>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/jobs" onClick={handleMobileClose}
                sx={{ color: 'rgba(255,255,255,0.8)' }}>
                <ListItemText primary="Jobs" />
              </ListItemButton>
            </ListItem>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.5 }} />
            <ListItem disablePadding>
              <ListItemButton onClick={() => { handleDocsClick('/api/stt/docs'); handleMobileClose(); }}
                sx={{ color: 'rgba(255,255,255,0.8)' }}>
                <ListItemText primary="Docs – STT API" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { handleDocsClick('/api/identity/docs'); handleMobileClose(); }}
                sx={{ color: 'rgba(255,255,255,0.8)' }}>
                <ListItemText primary="Docs – Identity API" />
              </ListItemButton>
            </ListItem>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.5 }} />
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/account" onClick={handleMobileClose}
                sx={{ color: 'rgba(255,255,255,0.8)' }}>
                <ListItemText primary="Account" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { handleLogout(); handleMobileClose(); }}
                sx={{ color: 'rgba(255,255,255,0.6)' }}>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/login" onClick={handleMobileClose}
                sx={{ color: 'rgba(255,255,255,0.8)' }}>
                <ListItemText primary="Log in" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/register" onClick={handleMobileClose}
                sx={{ color: '#1976d2', fontWeight: 600 }}>
                <ListItemText primary="Sign up"
                  primaryTypographyProps={{ fontWeight: 600, color: '#1976d2' }} />
              </ListItemButton>
            </ListItem>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.5 }} />
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/terms" onClick={handleMobileClose}
                sx={{ color: 'rgba(255,255,255,0.6)' }}>
                <ListItemText primary="Terms" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { handleDocsClick('/api/stt/docs'); handleMobileClose(); }}
                sx={{ color: 'rgba(255,255,255,0.6)' }}>
                <ListItemText primary="Docs – STT API" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { handleDocsClick('/api/identity/docs'); handleMobileClose(); }}
                sx={{ color: 'rgba(255,255,255,0.6)' }}>
                <ListItemText primary="Docs – Identity API" />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <AppBar
      position="static"
      data-test-id="navbar"
      sx={{
        bgcolor: '#0a0a0a',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: 'none',
      }}
    >
      <Toolbar sx={{ minHeight: '56px !important', py: 1, px: { xs: 2, md: 4 } }}>

        {/* Logo */}
        <Box
          component={Link}
          to="/"
          data-test-id="navbar-logo"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            mr: { xs: 2, md: 4 },
            '&:hover': { opacity: 0.8 },
          }}
        >
          <Box
            component="img"
            src="/logo.svg"
            alt="SovaVoice Logo"
            sx={{ width: 32, height: 32, mr: 2, filter: 'invert(1)' }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            sovavoice
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* ── Desktop nav (hidden on mobile) ── */}
        {isLoggedIn ? (
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            <Button
              color="inherit"
              component={Link}
              to="/dashboard"
              data-test-id="nav-dashboard"
              sx={{
                textTransform: 'none', fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
              }}
            >
              Dashboard
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/jobs"
              data-test-id="nav-jobs"
              sx={{
                textTransform: 'none', fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.8)', ml: 1,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
              }}
            >
              Jobs
            </Button>
            <Box>
              <Button
                color="inherit"
                onClick={handleDocsMenuOpen}
                data-test-id="nav-docs"
                sx={{
                  textTransform: 'none', fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.8)', ml: 1,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                }}
              >
                Docs
              </Button>
              <Menu
                anchorEl={docsAnchorEl}
                open={docsMenuOpen}
                onClose={handleDocsMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem data-test-id="nav-docs-stt" onClick={() => handleDocsClick('/api/stt/docs')}>STT Service API</MenuItem>
                <MenuItem data-test-id="nav-docs-identity" onClick={() => handleDocsClick('/api/identity/docs')}>Identity Service API</MenuItem>
              </Menu>
            </Box>
            <Tooltip title={userEmail || 'Account'} arrow>
              <Box
                component={Link}
                to="/account"
                data-test-id="nav-account"
                sx={{
                  ml: 1, display: 'flex', alignItems: 'center',
                  textDecoration: 'none', borderRadius: '50%', transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.1)', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)' },
                }}
              >
                <Box
                  sx={{
                    position: 'relative', width: 40, height: 40,
                    borderRadius: '50%', overflow: 'hidden',
                    border: '2px solid', borderColor: 'rgba(255, 255, 255, 0.3)',
                    bgcolor: 'primary.main',
                  }}
                >
                  {!avatarError && (
                    <Box
                      component="img"
                      src="/logo.svg"
                      alt="Owl Avatar"
                      onError={() => setAvatarError(true)}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }}
                    />
                  )}
                  {userEmail && (
                    <Box
                      sx={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: avatarError ? 'primary.main' : 'rgba(0, 0, 0, 0.6)',
                        color: 'white', fontSize: '1.5rem', fontWeight: 700,
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)', pointerEvents: 'none',
                      }}
                    >
                      {userEmail.charAt(0).toUpperCase()}
                    </Box>
                  )}
                </Box>
              </Box>
            </Tooltip>
            <Button
              color="inherit"
              onClick={handleLogout}
              data-test-id="nav-logout"
              sx={{
                textTransform: 'none', fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.8)', ml: 3,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
              }}
            >
              Logout
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            <Button
              color="inherit"
              component={Link}
              to="/terms"
              data-test-id="nav-terms"
              sx={{
                textTransform: 'none', fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.6)', ml: 1,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
              }}
            >
              Terms
            </Button>
            <Box>
              <Button
                color="inherit"
                onClick={handleDocsMenuOpen}
                sx={{
                  textTransform: 'none', fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.6)', ml: 1,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                }}
              >
                Docs
              </Button>
              <Menu
                anchorEl={docsAnchorEl}
                open={docsMenuOpen}
                onClose={handleDocsMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuItem onClick={() => handleDocsClick('/api/stt/docs')}>STT Service API</MenuItem>
                <MenuItem onClick={() => handleDocsClick('/api/identity/docs')}>Identity Service API</MenuItem>
              </Menu>
            </Box>
            <Box sx={{ ml: 3, display: 'flex', gap: 1 }}>
              <Button
                color="inherit"
                component={Link}
                to="/login"
                data-test-id="nav-login"
                sx={{
                  px: 2.5, py: 0.75, textTransform: 'none', fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                }}
              >
                Log in
              </Button>
              <Button
                variant="contained"
                component={Link}
                to="/register"
                data-test-id="nav-signup"
                sx={{
                  px: 2.5, py: 0.75, textTransform: 'none', fontWeight: 600,
                  borderRadius: 1.5, bgcolor: '#1976d2', color: '#ffffff',
                  '&:hover': { bgcolor: '#1565c0' },
                }}
              >
                Sign up
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Hamburger (mobile only) ── */}
        <IconButton
          color="inherit"
          onClick={() => setMobileOpen(true)}
          sx={{ display: { xs: 'flex', sm: 'none' } }}
          aria-label="open menu"
          data-test-id="nav-mobile-menu"
        >
          <MenuIcon />
        </IconButton>

        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={handleMobileClose}
          PaperProps={{ sx: { bgcolor: '#0a0a0a', color: 'white' } }}
        >
          {mobileDrawer}
        </Drawer>

      </Toolbar>
    </AppBar>
  );
}

export default Navbar;
